import {
    Multiselect,
    MultiselectProps,
    Select,
    SelectProps
} from '@cloudscape-design/components';
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalState } from '../../pages/context/GlobalStateContext';
import { ItemGate } from '../../resources/types';


interface SelectGateSingleProps {
    single: true; //just true
    value?: number; // Single ID
    width?: number;
    onSelect?: (selected: number | null) => void; // <--- CHANGED: Returns ID or null
    readonly?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
}

// When single = false (or undefined, as per union type)
interface SelectGateMultiProps {
    single?: false; // default is "false"
    value?: number[]; // Array of IDs
    width?: number;
    onSelect?: (selected: number[]) => void; // <--- CHANGED: Returns array of IDs
    readonly?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
}

export type SelectGateProps =
    | SelectGateSingleProps
    | SelectGateMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectGate: React.FC<SelectGateProps> = (props) => {
    const {
        single,
        value,
        width = 200,
        readonly,
        tokenLimit = 3,
        hideTokens = false,
        onSelect
    } = props;

    const { data } = useGlobalState();
    if (!data || !data.gates) return <></>;

    // State for Single Selection (holds the selected object)
    const [selectedSingle, setSelectedSingle] = useState<ItemGate | null>(null);

    // State for Multi Selection (holds the array of selected objects)
    const [selectedMulti, setSelectedMulti] = useState<ItemGate[]>([]);

    // --- Mapped Categories (Options List) ---

    // Memoize the calculation of available options
    const mappedGates: ItemGate[] = useMemo(() => {
        let gates: ItemGate[] = data.gates
            .filter((plan: ItemGate) => 1 === 1)
            .map((plan: ItemGate) => ({
                idGate: plan.idGate,
                gate: plan.gate,
                value: plan.idGate.toString(),
                label: plan.gate
            }));
        return gates;
    }, [data.gates]);
    // --- Initialization & Value Sync Effect ---

    useEffect(() => {
        if (!value) {
            setSelectedSingle(null);
            setSelectedMulti([]);
            return;
        }

        if (single) {
            // SINGLE MODE: Value is a number (ID)
            const id = value as number;
            const match = mappedGates.find(gate => gate.idGate === id) || null;
            setSelectedSingle(match);

        } else {
            // MULTI MODE: Value is an array of numbers (IDs)
            const ids = value as number[];
            const matches = mappedGates.filter(gate => ids.includes(gate.idGate));
            setSelectedMulti(matches);
        }
    }, [value, single, mappedGates]);


    // --- Single Select Handler ---
    const handleSingleChange: SelectProps['onChange'] = ({ detail }) => {
        const selectedOption = detail.selectedOption as ItemGate | null;

        setSelectedSingle(selectedOption);

        if (onSelect && single) {
            const selectedId = selectedOption?.idGate ?? null; // Extract ID
            // FIX: Assert the function signature directly to satisfy the compiler
            (onSelect as (selected: number | null) => void)(selectedId);
        }
    };

    // --- Multi Select Handler ---
    const handleMultiChange: MultiselectProps['onChange'] = ({ detail }) => {
        const list = detail.selectedOptions as ItemGate[];

        // Use spread to ensure setting a new array reference
        setSelectedMulti([...list]);

        if (onSelect && !single) {
            const selectedIds = list.map(item => item.idGate); // Extract array of IDs
            // FIX: Assert the function signature directly to satisfy the compiler
            (onSelect as (selected: number[]) => void)(selectedIds);
        }
    };

    // --- Rendering ---

    const placeholder = single ? "Seleccionar puerta" : "Seleccionar puertas";
    const wrapperStyle: React.CSSProperties = {
        width: width > 0 ? `${width}px` : '100%'
    };
    return (
        <div style={wrapperStyle}>
            {single ? (
                // SINGLE SELECTION
                <Select
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOption={selectedSingle}
                    options={mappedGates}
                    onChange={handleSingleChange}
                />
            ) : (
                // MULTI SELECTION
                <Multiselect
                    tokenLimit={tokenLimit}
                    hideTokens={hideTokens}
                    readOnly={readonly}
                    placeholder={selectedMulti.length === 0 ? placeholder : `${selectedMulti.length}/${mappedGates.length} seleccionadas`}
                    selectedOptions={selectedMulti}
                    options={mappedGates}
                    onChange={handleMultiChange}
                />
            )}

        </div>
    );
};

export default SelectGate;